import { useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import React, { useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ScheduleReviewSection from '../common/Scheduling/ScheduleReviewSection'
import { FORM_FIELDS } from './constants'
import { FormOptionsContext } from './useFormOptions'

const WARNING_MESSAGE_FIELDS = ['code', 'message', 'warningMessage', 'warning', 'description']
const WARNING_MESSAGE_ID_PREFIX = 'ENGAGE.OFFBOARD.WARNING.'

const propTypes = {
  /**
   * Indicates if offboarding was complete or not
   * Used to show the check icons in the list
   */
  isUserOffboarded: PropTypes.bool.isRequired,
  /**
   * Employee full name
   */
  employeeFullName: PropTypes.string.isRequired,
  /**
   * information of the user to be removed
   */
  successWarnings: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  userInfo: PropTypes.object, // eslint-disable-line react/forbid-prop-types
}

const getWarningText = (warning) => {
  if (typeof warning === 'string') {
    return warning
  }

  if (!warning || typeof warning !== 'object') {
    return ''
  }

  const matchingField = WARNING_MESSAGE_FIELDS.find((field) => typeof warning?.[field] === 'string')
  return matchingField ? warning[matchingField] : ''
}

const getWarningMessages = (warning) => {
  if (Array.isArray(warning)) {
    return warning.map(getWarningText).filter(Boolean)
  }

  const message = getWarningText(warning)
  return message ? [message] : []
}

const hasWarningMessages = (warning) => getWarningMessages(warning).length > 0

const normalizeWarnings = (warnings) => {
  if (!warnings) {
    return {}
  }

  if (Array.isArray(warnings)) {
    return warnings.reduce((accumulator, warning) => {
      const context = warning?.context
      const message = getWarningText(warning)

      if (context && message) {
        accumulator[context] = message
      }

      return accumulator
    }, {})
  }

  if (typeof warnings !== 'object') {
    return {}
  }

  return Object.entries(warnings).reduce((accumulator, [key, warning]) => {
    const messages = getWarningMessages(warning)

    if (messages.length) {
      accumulator[key] = messages.length === 1 ? messages[0] : messages
    }

    return accumulator
  }, {})
}

function ReviewContent(props) {
  const { isUserOffboarded, employeeFullName, successWarnings, userInfo } = props
  const { delegatedPermissions, emailAlias } = userInfo
  const intl = useIntl()

  const { values } = useFormikContext()

  const { m365GroupOptions, securityGroupOptions, distributionGroupOptions, sharedMailboxOptions } =
    useContext(FormOptionsContext)

  const isEmailAndOneDriveSectionEmpty =
    !values[FORM_FIELDS.REMOVE_EMAIL_ALIASES] &&
    !values[FORM_FIELDS.REMOVE_DELEGATE_PERMISSIONS] &&
    !values[FORM_FIELDS.FORWARD_MAILBOX_TO] &&
    !values[FORM_FIELDS.HIDDEN_FROM_ADDRESS_LIST_ENABLED]

  const isSecurityActionsSectionEmpty =
    !values[FORM_FIELDS.RESET_PASSWORD] && !values[FORM_FIELDS.REVOKE_SESSIONS] && !values[FORM_FIELDS.DISABLE_USER]

  const warningsByContext = normalizeWarnings(successWarnings)
  const generalWarningMessage = warningsByContext.removeLicenses
  const emailAliasesWarningMessage = warningsByContext.emailAliasesToBeRemoved
  const delegatePermissionsWarningMessage = warningsByContext.removeDelegatePermissions
  const forwardMailboxWarningMessage = warningsByContext.forwardMailboxTo
  const hiddenFromAddressListWarningMessage = warningsByContext.hiddenFromAddressListEnabled
  const sharedMailboxConfigWarningMessage = warningsByContext.sharedMailboxConfig
  const automaticRepliesWarningMessage = warningsByContext.automaticRepliesSetting
  const groupsWarningMessage = warningsByContext.groupsToBeRemoved
  const distributionGroupsWarningMessage = warningsByContext.distributionGroupsToBeRemoved
  const sharedMailboxesWarningMessage = warningsByContext.sharedMailboxesToBeRemoved

  return (
    <>
      <ScheduleReviewSection />

      <ReviewSectionTitle
        title={isUserOffboarded ? 'ENGAGE.OFFBOARD.USER_REMOVED' : 'ENGAGE.OFFBOARD.USER_TO_BE_REMOVED'}
        isUserOffboarded={isUserOffboarded}
      />

      <ul>
        <li>{employeeFullName}</li>
      </ul>

      {generalWarningMessage && (
        <>
          <ReviewSectionTitle title="GENERAL.GENERAL" isUserOffboarded={isUserOffboarded} hasWarning />
          <ul>
            <ReviewListItem warningMessage={generalWarningMessage} intl={intl} />
          </ul>
        </>
      )}

      {!isEmailAndOneDriveSectionEmpty && (
        <>
          <ReviewSectionTitle
            title="GENERAL.EMAIL"
            isUserOffboarded={isUserOffboarded}
            hasWarning={
              hasWarningMessages(emailAliasesWarningMessage) ||
              hasWarningMessages(delegatePermissionsWarningMessage) ||
              hasWarningMessages(forwardMailboxWarningMessage) ||
              hasWarningMessages(hiddenFromAddressListWarningMessage)
            }
          />
          <ul>
            {values[FORM_FIELDS.REMOVE_EMAIL_ALIASES] && (
              <ReviewListItem warningMessage={emailAliasesWarningMessage} intl={intl}>
                <FormattedMessage
                  id={isUserOffboarded ? 'ENGAGE.OFFBOARD.ALIASES_REMOVED' : 'ENGAGE.OFFBOARD.ALIASES_TO_BE_REMOVED'}
                />
                &nbsp;{emailAlias?.join(', ')}
              </ReviewListItem>
            )}

            {values[FORM_FIELDS.REMOVE_DELEGATE_PERMISSIONS] && (
              <ReviewListItem warningMessage={delegatePermissionsWarningMessage} intl={intl}>
                <FormattedMessage
                  id={
                    isUserOffboarded ? 'ENGAGE.OFFBOARD.DELEGATES_REMOVED' : 'ENGAGE.OFFBOARD.DELEGATES_TO_BE_REMOVED'
                  }
                />
                &nbsp;{delegatedPermissions?.join(', ')}
              </ReviewListItem>
            )}

            {values[FORM_FIELDS.FORWARD_MAILBOX_TO] && (
              <ReviewListItem warningMessage={forwardMailboxWarningMessage} intl={intl}>
                <FormattedMessage id="ENGAGE.OFFBOARD.FORWARD_EMAILS_TO" />
                &nbsp;{values[FORM_FIELDS.FORWARD_MAILBOX_TO]}
              </ReviewListItem>
            )}

            {values[FORM_FIELDS.HIDDEN_FROM_ADDRESS_LIST_ENABLED] && (
              <ReviewListItem warningMessage={hiddenFromAddressListWarningMessage} intl={intl}>
                <FormattedMessage id="ENGAGE.OFFBOARD.HIDDEN_FROM_ADDRESS_LIST_ENABLED" />
              </ReviewListItem>
            )}
          </ul>
        </>
      )}

      {values[FORM_FIELDS.CONVERT_TO_SHARED_MAILBOX] && (
        <>
          <ReviewSectionTitle
            title="ENGAGE.OFFBOARD.CONVERT_TO_SHARED_MAILBOX"
            isUserOffboarded={isUserOffboarded}
            hasWarning={hasWarningMessages(sharedMailboxConfigWarningMessage)}
          />
          <ul>
            <ReviewListItem warningMessage={sharedMailboxConfigWarningMessage} intl={intl}>
              <FormattedMessage id="ENGAGE.OFFBOARD.ACCESS_GIVEN_TO" />
              &nbsp;{values[FORM_FIELDS.MAILBOX_USERS]?.join(', ')}
            </ReviewListItem>
          </ul>
        </>
      )}

      {values[FORM_FIELDS.SEND_AUTOMATIC_REPLIES] && (
        <>
          <ReviewSectionTitle
            title="ENGAGE.OUT_OF_OFFICE_SEND_AUTO_REPLIES"
            isUserOffboarded={isUserOffboarded}
            hasWarning={hasWarningMessages(automaticRepliesWarningMessage)}
          />
          <ul>
            {automaticRepliesWarningMessage ? (
              <ReviewListItem warningMessage={automaticRepliesWarningMessage} intl={intl} />
            ) : (
              <>
                {values[FORM_FIELDS.AUTO_REPLY_START_DATE] && (
                  <li>
                    <FormattedMessage id="ENGAGE.OUT_OF_OFFICE_START_TIME" />
                    &nbsp;{new Date(values[FORM_FIELDS.AUTO_REPLY_START_DATE]).toLocaleString()}
                  </li>
                )}
                {values[FORM_FIELDS.AUTO_REPLY_END_DATE] && (
                  <li>
                    <FormattedMessage id="ENGAGE.OUT_OF_OFFICE_END_TIME" />
                    &nbsp;{new Date(values[FORM_FIELDS.AUTO_REPLY_END_DATE]).toLocaleString()}
                  </li>
                )}
                {values[FORM_FIELDS.INTERNAL_REPLY_MESSAGE] && (
                  <li>
                    <FormattedMessage id="ENGAGE.OUT_OF_OFFICE_SEND_MESSAGE_ORGANIZATION" />:
                    <pre className="mb-0 font-size-default text-pre-wrap">
                      {values[FORM_FIELDS.INTERNAL_REPLY_MESSAGE]}
                    </pre>
                  </li>
                )}
                {values[FORM_FIELDS.EXTERNAL_REPLY_MESSAGE] && (
                  <li>
                    <FormattedMessage id="ENGAGE.OUT_OF_OFFICE_SEND_MESSAGE_EXTERNAL" />:
                    <pre className="mb-0 font-size-default text-pre-wrap">
                      {values[FORM_FIELDS.EXTERNAL_REPLY_MESSAGE]}
                    </pre>
                  </li>
                )}
              </>
            )}
          </ul>
        </>
      )}

      {!isSecurityActionsSectionEmpty && (
        <>
          <ReviewSectionTitle title="ENGAGE.OFFBOARD.SECURITY_ACTIONS" isUserOffboarded={isUserOffboarded} />
          <ul>
            {values[FORM_FIELDS.RESET_PASSWORD] && (
              <li>
                <FormattedMessage id="ENGAGE.RESET_PASSWORD" />
              </li>
            )}

            {values[FORM_FIELDS.REVOKE_SESSIONS] && (
              <li>
                <FormattedMessage id="ENGAGE.SIGN_USER_OUT_OF_ALL_APPS" />
              </li>
            )}

            {values[FORM_FIELDS.DISABLE_USER] && (
              <li>
                <FormattedMessage id="ENGAGE.BLOCK_USER_FROM_SIGNING_IN" />
              </li>
            )}
          </ul>
        </>
      )}

      <GroupReviewSection
        selected={values[FORM_FIELDS.REMOVE_FROM_M365_GROUPS]}
        options={m365GroupOptions}
        isUserOffboarded={isUserOffboarded}
        sectionTitle="ENGAGE.OFFBOARD.REMOVE_FROM_GROUPS"
        removedTitle="ENGAGE.OFFBOARD.REMOVE_FROM_GROUPS_COLON"
        keptTitle="ENGAGE.OFFBOARD.KEEP_IN_GROUPS"
        warningMessage={groupsWarningMessage}
        intl={intl}
      />

      <GroupReviewSection
        selected={values[FORM_FIELDS.REMOVE_FROM_SECURITY_GROUPS]}
        options={securityGroupOptions}
        isUserOffboarded={isUserOffboarded}
        sectionTitle="ENGAGE.OFFBOARD.REMOVE_FROM_SECURITY_GROUPS"
        removedTitle="ENGAGE.OFFBOARD.REMOVE_FROM_SECURITY_GROUPS_COLON"
        keptTitle="ENGAGE.OFFBOARD.KEEP_IN_SECURITY_GROUPS"
        warningMessage={groupsWarningMessage}
        intl={intl}
      />

      <GroupReviewSection
        selected={values[FORM_FIELDS.REMOVE_FROM_DISTRIBUTION_GROUPS]}
        options={distributionGroupOptions}
        isUserOffboarded={isUserOffboarded}
        sectionTitle="ENGAGE.OFFBOARD.REMOVE_FROM_DISTRIBUTION_GROUPS"
        removedTitle="ENGAGE.OFFBOARD.REMOVE_FROM_DISTRIBUTION_GROUPS_COLON"
        keptTitle="ENGAGE.OFFBOARD.KEEP_IN_DISTRIBUTION_GROUPS"
        warningMessage={distributionGroupsWarningMessage}
        intl={intl}
      />

      <GroupReviewSection
        selected={values[FORM_FIELDS.REMOVE_FROM_SHARED_MAILBOXES]}
        options={sharedMailboxOptions}
        isUserOffboarded={isUserOffboarded}
        sectionTitle="ENGAGE.OFFBOARD.REMOVE_FROM_SHARED_MAILBOXES"
        removedTitle="ENGAGE.OFFBOARD.REMOVE_FROM_SHARED_MAILBOXES_COLON"
        keptTitle="ENGAGE.OFFBOARD.KEEP_IN_SHARED_MAILBOXES"
        warningMessage={sharedMailboxesWarningMessage}
        intl={intl}
      />
    </>
  )
}

ReviewContent.propTypes = propTypes

export default ReviewContent

// SUPPORT COMPONENTS
/* eslint-disable react/prop-types */
function ReviewSectionTitle(props) {
  const { isUserOffboarded, title, titleText, hasWarning } = props
  return (
    <div className="text-strong">
      {isUserOffboarded &&
        (hasWarning ? (
          <span className="font-warning fas fa-triangle-exclamation me-2" />
        ) : (
          <span className="font-color-engage fas fa-circle-check me-2" />
        ))}
      {title ? <FormattedMessage id={title} /> : titleText}
    </div>
  )
}

function renderWarningMessage(warningMessage, intl) {
  if (!warningMessage) {
    return null
  }

  if (warningMessage.startsWith(WARNING_MESSAGE_ID_PREFIX)) {
    return intl.formatMessage({ id: warningMessage })
  }

  return warningMessage
}

function WarningListItems({ warningMessage, intl }) {
  return getWarningMessages(warningMessage).map((message) => (
    <li key={message}>{renderWarningMessage(message, intl)}</li>
  ))
}

function ReviewListItem({ warningMessage, children, intl }) {
  if (getWarningMessages(warningMessage).length) {
    return <WarningListItems warningMessage={warningMessage} intl={intl} />
  }

  return <li>{children}</li>
}

function GroupReviewSection({
  selected,
  options,
  isUserOffboarded,
  sectionTitle,
  removedTitle,
  keptTitle,
  warningMessage,
  intl,
}) {
  if (!options?.length) {
    return null
  }

  const kept = []
  const removed = []

  options.forEach((option) => {
    if (selected.includes(option.value)) {
      removed.push(option.label)
    } else {
      kept.push(option.label)
    }
  })

  return (
    <>
      <ReviewSectionTitle
        title={sectionTitle}
        isUserOffboarded={isUserOffboarded}
        hasWarning={hasWarningMessages(warningMessage)}
      />
      <ul>
        {hasWarningMessages(warningMessage) ? (
          <WarningListItems warningMessage={warningMessage} intl={intl} />
        ) : (
          <>
            <li>
              <FormattedMessage id={removedTitle} />
              &nbsp;
              {removed.length ? removed.join(', ') : 'None'}
            </li>
            <li>
              <FormattedMessage id={keptTitle} />
              &nbsp;
              {kept.length ? kept.join(', ') : 'None'}
            </li>
          </>
        )}
      </ul>
    </>
  )
}
